// #region imports
import { CaseState, ViewMode } from 'app/area/case/types-store';
import {
  ChildItemSelection,
  ReceiveCasePermissions,
  ChangeViewMode
} from 'app/area/case/action-types';
import { actionCreators as caseActionCreators } from 'app/area/case/actions';
import { caseTranslations, viewModeCacheKey } from 'app/area/case/constants';
import { getLocationOfInitiator } from 'app/area/case/draft/connected-state';
import { defaultValueHandler } from 'app/area/shared/default-value-handler';
import { replace } from 'app/area/shared/navigation';
import * as navigate from 'app/area/shared/navigation';
import { maxChildEntityId, getCaseContextId as getCaseContextIdStoreData } from 'app/infrastructure/store/query';
import { caseTypeId as getCaseTypeId, caseContextId as getCaseContextId } from 'app/infrastructure/data/wireformat';
import { debug } from 'app/infrastructure/log';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { errorSafe, LoggedError, whenIdle } from 'app/infrastructure/redux';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import {
  BackgroundSaveComplete,
  BeginBackgroundSave,
  BeginCaseSave,
  CaseSaveFailed,
  ReceiveCodetable,
  RequestCaseSave,
  RequestCodetable,
  RequestCodetableError,
  RequestImmediateBackgroundSave,
  ValueChange,
  BackgroundSaveFailed,
  ReceiveCodetableCategory,
  RequestCodetableCategory,
  RequestSaveAsDraft,
  SaveAsDraftCompleted
} from 'app/infrastructure/ui/form/action-types';
import {
  caseDataToStore as toStore,
  caseMetadataToStore as toMetadata,
  storeToCaseData as fromStore
} from 'app/infrastructure/store/normalize';
import {
  AddDocumentItemOptions,
  CaseConnectionParam,
  OptionsRequestContext,
  OptionsRequestParams,
  UiState,
  CopyDocumentItemOptions
} from 'app/infrastructure/ui/form/types';
import { ApplicationState } from 'app/types-store';
import {
  CompositeKeys,
  RenderMode,
  StoreField,
  StoreItemPath,
  ActionDispatching,
  CasePermissions
} from 'app/types-shared';
import get from 'lodash/get';

import { log } from 'app/infrastructure/log';
import { RemoteResult } from 'app/infrastructure/remote-data/http';
import {
  AddDocumentItem,
  DeleteDocumentItem,
  EnterConnectedDraftEdit,
  EnterDraftEdit,
  FailDraftDocument,
  LeaveDraftEdit,
  ReceiveDraftDocument,
  RequestDraftDocument,
  CopyDocumentItem,
  ShowConfirmWarnings,
  WarningsConfirmed,
  ReceiveUiDefinition
} from './action-types';
import { api } from './api';
import { api as connectedCaseApi } from 'app/area/case/features/connected-cases/api';
import {
  routeFromStoreItemPath,
  sectionFromStoreItemPath
} from 'app/infrastructure/store/entity-route';
import { getCaseId, getDraftId } from 'app/infrastructure/store';
import { clientCache } from 'app/infrastructure/universal';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { resolveHasAnyValidations, resolveWarnings } from 'app/infrastructure/ui/renderer/resolvers';
import { AutoCloseDuration, toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { getItemsBeforeKey, inStartupDashboard, isCaseViewModalOpen } from 'app/components/helpers';
//#endregion

const draftWillBeConnected = (state: CaseState) =>
  !!get(state, 'editing.connectedDrafts.length');

const readAccessToSubmittedCase = (
  caseId: number,
  casePermissions: CasePermissions[]
) =>
  casePermissions.length > 0 &&
  (casePermissions.find(a => a.caseId === caseId) || {}).read;

/**
 * A higher order function that can handle messages related to saving and submitting drafts
 *
 * This code is not cleanly separated, and needs more work
 *
 * @param dispatch
 * @param getState
 */
const createErrorHandler = (dispatch: any, getState: any) => (
  error: RemoteResult,
  customErrorMessage?: string
) => {
  const code = get(error, 'response.status');
  const draftNotAvaliable = code > 400 && code <= 499;
  const isValidationError =
    code === 400 && error.validationErrors && error.validationErrors.length;

  // To ensure the user gets a notification even after navigation,
  // if draft was deleted under our feets, or other unexpected errors occured
  !isValidationError && dispatch(new LoggedError(error));

  if (draftNotAvaliable) {
    const state = getState().caseArea;
    const caseNo = state.editing && getCaseId(state.editing.values);

    const connectedCase =
      state.editing && getLocationOfInitiator(state.editing.connectedDrafts);
    if (connectedCase) {
      replace(connectedCase);
      return;
    }

    dispatch(new LeaveDraftEdit());

    if (caseNo) {
      // If an existing case was beeing edited, return to read mode
      navigate.toCaseViewInternal(caseNo);
    } else {
      navigate.toStartupView(true);
    }
  } else {
    // this should probly be a concern of the calling action creator, but edge cases makes this correct for now
    const { userMessages, validationErrors } = error;
    const serviceMessages =
      userMessages && userMessages.length
        ? userMessages.map(m => m.message).join('\n')
        : undefined;
    const validationErrorsMessages = isValidationError && 
      validationErrors && validationErrors.length
        ? validationErrors.map(m => m.message).join('\n')
        : undefined;
    dispatch(
      new ErrorNotification(
        customErrorMessage || serviceMessages || validationErrorsMessages || error.message
      )
    );
  }
};

async function loadDraft(dispatch: any, draftId: number, caseState: UiState) {
  dispatch(new RequestDraftDocument(draftId));

  const res = await api.draft(draftId, !!caseState.editing && !!caseState.editing.loadValidationMetadata);
  const draft = res.json.data;
  const etag = res.etag;
  const entities = toStore(draft);
  const no = getCaseId(entities);

  dispatch(new ReceiveDraftDocument(draftId, entities, toMetadata(draft), etag));

  return { draft, no };
}

async function connectDraftAndSubmit(dispatch: any, state: UiState, utcOffset: number, translate: any, draftId: number, 
  useReverseConnection: boolean) {
  const editing = state.editing;
  const handleDraftErrors = createErrorHandler(dispatch, state);

  try {
    if (draftWillBeConnected(state)) {
      const prevItem = editing.connectedDrafts.slice(-1)[0];
      await connectedCaseApi.addCaseConnection(
        draftId,
        prevItem.caseId,
        prevItem.connectionTypeId,
        prevItem.sequence,
        editing.etag,
        useReverseConnection
      );
      await dispatch(actionCreators.requestImmediateSave());
    }
      await api.submitCase({
        caseDraftId: draftId,
        utcOffset
      });

      const parentCaseId = editing?.connectedDrafts?.slice(-1)[0]?.caseId;
      const casePermissions = await api.permissions(parentCaseId);

      if (!readAccessToSubmittedCase(parentCaseId, casePermissions)) {
        const successText = translate(caseTranslations.message_case_success);
        const notAuthorizedText = translate(
          caseTranslations.message_not_authorized_to_read_case
        );

        const toasts = toastFactory();
        toasts.dispatchSuccess(`${successText} - ${notAuthorizedText || ''}`);
      }
      navigate.toConnectionInitiator(getLocationOfInitiator(editing.connectedDrafts));
      dispatch(new LeaveDraftEdit());
      const toasts = toastFactory();
      const successText = translate(caseTranslations.message_connected_cases_success1);
      toasts.dispatchSuccess(successText);
  } catch (err) {
      handleDraftErrors(err);
      dispatch(new CaseSaveFailed());
  }
}

export const actionCreators = {
  /**
   * When user requests to load the draft to begin editing
   */
  editDraft: (
    draftId: number,
    connectToDraft?: CaseConnectionParam[]
  ): ActionDispatching<any, ApplicationState> => async (
    dispatch: any,
    getState: any
  ) => {
      let casePermissions = { edit: true, delete: true };
      const handleDraftError = createErrorHandler(dispatch, getState);

      try {
        const { draft, no } = await loadDraft(dispatch, draftId, getState);

        const caseType = getCaseTypeId(draft);
        const caseContextId = getCaseContextId(draft);
        dispatch(caseActionCreators.caseRiskMatrixDefinition(caseType));
        const uiDef = await dispatch(responseCachingActionCreators.getUiDefinition(caseType, caseContextId));

        if (!uiDef) {
          dispatch(new FailDraftDocument());
          return;
        }

        if (no) {
          casePermissions = (await api.permissions(no)) as any;
        }

        dispatch(new ReceiveCasePermissions(no, casePermissions as any));

        dispatch(new EnterDraftEdit(draftId, { ...uiDef, applicationId: caseType }));

        connectToDraft && dispatch(new EnterConnectedDraftEdit(connectToDraft));
      } catch (err) {
        handleDraftError(err);
        dispatch(new FailDraftDocument());
      }
    },

  /**
   * Components defining a onChange event gets an onChange callback assigned.
   * onChange calls into this action creator whenever the components value is changed.
   * The redux store will be updated with the new value;
   */
  valueChanged: (
    storePath: StoreItemPath[],
    dataTag: string,
    newValue: StoreField[],
    immediateSave?: boolean
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new ValueChange(storePath, dataTag, newValue));
      immediateSave && dispatch(actionCreators.requestImmediateSave());
    }),

  /**
   * Components defining an onLoadOptions event gets a callback assigned.
   * The callback assigned calls into this action creator to load code table data into the redux store.
   * The related reducer will store the data using the componentContext as id in a temporary store slice.
   */
  loadOptions: (
    componentContext: OptionsRequestContext,
    loadOptionsParams: OptionsRequestParams
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new RequestCodetable(componentContext, loadOptionsParams));

      try {
        const transNo = get(componentContext.path, '[0].tblTrans');
        //TODO: It might make more sense to send this down from whoever is calling this action, but I don't see an easy way to do it.
        //The way it is now it ties the "path" ordering to the concept of a transNo, which isn't optimal.

        const response: any = await api.options(
          transNo,
          componentContext.domainId,
          loadOptionsParams,
          !!loadOptionsParams.query
        );
        dispatch(
          new ReceiveCodetable(
            componentContext,
            loadOptionsParams,
            response ? response.data : null
          )
        );
      } catch (error) {
        dispatch(new LoggedError(error));
        dispatch(
          new RequestCodetableError(componentContext, loadOptionsParams)
        );
      }
    }),

  /**
   * Components defining an onLoadCategoryOptions event gets a callback assigned.
   * The callback assigned calls into this action creator to load code table data into the redux store.
   * The related reducer will store the data using the componentContext as id in a temporary store slice.
   */
  loadCategoryOptions: (
    componentContext: OptionsRequestContext,
    loadCategoryOptionsParams: OptionsRequestParams
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new RequestCodetableCategory(componentContext, loadCategoryOptionsParams));

      try {
        const transNo = get(componentContext.path, '[0].tblTrans');
        //TODO: It might make more sense to send this down from whoever is calling this action, but I don't see an easy way to do it.
        //The way it is now it ties the "path" ordering to the concept of a transNo, which isn't optimal.

        const response: any = await api.categoryOptions(
          transNo,
          componentContext.domainId,
          loadCategoryOptionsParams,
          !!loadCategoryOptionsParams.query
        );
        dispatch(
          new ReceiveCodetableCategory(
            componentContext,
            loadCategoryOptionsParams,
            response ? response.data : null
          )
        );
      } catch (error) {
        dispatch(new LoggedError(error));
        dispatch(
          new RequestCodetableError(componentContext, loadCategoryOptionsParams)
        );
      }
    }),

  /**
   *  This is the action creator invoked for components requesting injected action callback for "onCopyDocumentItem".
   */
  copyDocumentItem: (
    documentItemPath: StoreItemPath[],
    options: CopyDocumentItemOptions
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any) => {
      dispatch(new CopyDocumentItem(documentItemPath, options));
      dispatch(actionCreators.requestImmediateSave());
    }),

  /**
   * This is the action creator invoked for components requesting injected action callback for  "onAddDocumentItem".
   * Adds an entity of the specified entityType.
   * If no child id is provided in options, the default is to increment the max existing id by 1.
   */

  addDocumentItem: (
    parentPath: StoreItemPath[],
    entityType: string,
    selectAddedItem: boolean,
    options: AddDocumentItemOptions
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state = getState();

      if (!state.caseArea.editing) {
        // TODO:
        // Async operation (file-uploader) fires callback after navigation occured. UI is no longer in edit mode.
        // This would not happen if the operation was moved out of the component and into actionCreator.
        return;
      }

      const { childId, initialValues, defaultValues } = options;
      const { utcOffset } = state.app.settings;
      const { values } = state.caseArea.editing;

      const newItemNo =
        childId || maxChildEntityId(values, parentPath, entityType) + 1;

      await dispatch(
        new AddDocumentItem(parentPath, entityType, newItemNo, {
          primaryKey: { value: newItemNo },
          ...initialValues
        })
      );

      //field values with initialValues shouldn't be overridden with default values e.g. Loss potential evaluation
      const defaultValuesExcluded = defaultValues?.filter(d => Object.keys(initialValues || {}).includes(d.dataTag));
      const filteredDefaultValues = defaultValuesExcluded ? defaultValues?.filter(x => !defaultValuesExcluded.includes(x))
        .concat(defaultValuesExcluded.filter(x => !defaultValues.includes(x))) : defaultValues;

      if (filteredDefaultValues && filteredDefaultValues.length) {
        await Promise.all(
          filteredDefaultValues.map(async d => {
            const defaultValue = await defaultValueHandler(d.defaultValue, {
              utcOffset
            });

            // Check for undefined to support boolean values.
            defaultValue !== undefined &&
              dispatch(
                new ValueChange(
                  [...parentPath, { [entityType]: newItemNo }],
                  d.dataTag,
                  [defaultValue]
                )
              );

            return d;
          })
        );
      }

      dispatch(actionCreators.requestImmediateSave());

      if (selectAddedItem) {
        const newItem = [...parentPath, { [entityType]: newItemNo }];

        dispatch(actionCreators.selectDocumentItem(newItem));
      }
    }),

  /**
   * Components asking to have a injected action callback for "onDeleteDocumentItem", ¨this is the action invoked.
   * Marks the entity as deleted in the store, awaitig next background save or submit
   */
  deleteDocumentItem: (
    selectParentItem: boolean = true,
    childItemPath: StoreItemPath[]
  ): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const path = [...childItemPath];
      await dispatch(new DeleteDocumentItem(path));

      dispatch(actionCreators.requestImmediateSave());

      const parentItemPath = childItemPath.slice(0, -1);

      // Select parent item, or fallback to current draft
      selectParentItem && parentItemPath && dispatch(
        actionCreators.selectDocumentItem(parentItemPath || null)
      );
    }),

  /**
   * - Whenever rows are added in a table, or removed from a table,
   * the webclient should immediately run a background save (without waiting the normal 5 seconds for it).
   * - Whenever there is already a background save running,
   * there should be queued a new background save the moment the previous one finishes.
   */
  requestImmediateSave: (): ActionDispatching<any, any> => async (
    dispatch: any,
    getState: () => any
  ) => {
    dispatch(new RequestImmediateBackgroundSave());
  },

  /**
   * The ActionCreator passed to the middleware that handles background saving.
   * This actionCreator can only be called by the save middleware.
   */
  backgroundSave: (): ActionDispatching<any, any> => async (
    dispatch: any,
    getState: () => any
  ) => {
    debug('saving...');

    const handleDraftErrors = createErrorHandler(dispatch, getState);
    const state = getState().caseArea as UiState;
    const caseData = fromStore(state.editing.values);

    try {
      dispatch(new BeginBackgroundSave());

      const response = await api.saveDraft(
        caseData,
        state.editing.etag,
        !!state.editing.loadValidationMetadata
      );
      const draft = response.json.data;
      const etag = response.etag;

      const currentCaseContextId = getCaseContextIdStoreData(state.editing.values);
      const nextCaseContextId = getCaseContextId(draft);
      const caseType = getCaseTypeId(draft);
      if (currentCaseContextId !== nextCaseContextId) {
        // We need to load new ui definition
        const uiDef = await dispatch(responseCachingActionCreators.getUiDefinition(caseType, nextCaseContextId));
        dispatch(new ReceiveUiDefinition(uiDef));
      }

      dispatch(
        new BackgroundSaveComplete(toStore(draft), toMetadata(draft), etag)
      );
    } catch (error) {
      dispatch(new BackgroundSaveFailed(error));
      handleDraftErrors(error);
    }
  },

  changeViewMode: (mode: ViewMode): ActionDispatching<any, any> =>
    errorSafe((dispatch: any, getState: any) => {
      clientCache.setItem(viewModeCacheKey, mode);
      dispatch(new ChangeViewMode(mode));
    }),

  /**
   * An item in a child table of a case is selected by the user.
   * Ensure this is reflected in the state and routing
   */
  selectDocumentItem: (childItem: CompositeKeys): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state = getState().caseArea;

      dispatch(new ChildItemSelection(childItem));
           
      const itemsBeforeTrans = getItemsBeforeKey(childItem, 'tblTrans');

      const draftPathPrefix = document.location.pathname.split('/case')?.[0] + '/case';
      const appendCasePathPrefix = !itemsBeforeTrans.length && isCaseViewModalOpen() && !!draftPathPrefix.length;
      const parentRoute = appendCasePathPrefix ? draftPathPrefix : '';

      const params = new URLSearchParams(document.location.search.substring(1));
      const currentSection = params.get('section');

      // if there is no selection, fallback to the current case
      const draft = !childItem && [
        { tblTrans: getDraftId(state.editing.values) }
      ];
      const childOfCase = routeFromStoreItemPath(
        childItem || draft,
        RenderMode.edit
      );

      const { renderMode, uiDefinition } = getState().caseArea;
      const section = sectionFromStoreItemPath(
        childItem,
        renderMode,
        uiDefinition
      );

      if (childItem?.length > 1 || !currentSection) {
        navigate.toChildItem(childOfCase, section && section.id, true, parentRoute);
      }
      else {
        navigate.toChildItem(childOfCase, parseInt(currentSection, 10), true, parentRoute);
      }
    }),

  /**
   * Sets the flag that submit is about to begin.
   * To handle pending background saves.
   */
  onSubmit: (continueEditing: boolean = false): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new ChildItemSelection(null));
      dispatch(new BeginCaseSave(continueEditing));
    }),

  /**
   * Interupt editing by navigating away
   */
  onCancel: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state: CaseState = getState().caseArea;
      const caseId = getCaseId(state.editing.values);

      const pendingConnectedDraft = getLocationOfInitiator(
        state.editing.connectedDrafts
      );
      if (pendingConnectedDraft) {
        replace(pendingConnectedDraft);
        return;
      }

      if (caseId) {
        isCaseViewModalOpen() ? navigate.toCaseViewModal(caseId as any) : navigate.toCaseView(caseId as any, true);
        
      }
      else {//doesn't know where to navigate as caseid doesn't exists
          isCaseViewModalOpen() && !inStartupDashboard() ? navigate.toParentPathFromCaseViewModal() : navigate.toStartupView(true);
      }
    }),

  /**
   * Delete the active draft document on server
   */
  deleteDraft: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state = getState().caseArea;
      const draftData = state.editing.values;
      const draft = getDraftId(draftData);

      dispatch(new LeaveDraftEdit());
      const result = (await api.deleteDraft(draft, state.editing.etag)) as any;

      if (result && result.status === 204) {
        log(`Draft ${draft} deleted`);
      }
    }),

    
  saveAsDraft: (showDraftAfterSave: boolean = false): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state = getState().caseArea as UiState;
      const draftId = getDraftId(state.editing.values);

      await dispatch(new RequestSaveAsDraft());
      await dispatch(actionCreators.requestImmediateSave());
      await dispatch(
        whenIdle(async (_: any) => {
          if (showDraftAfterSave) {
            navigate.toCaseSaveAsDraftView(draftId);
          }
          else {
            navigate.toStartupView(true, { fromSaveAsDraft: true, draftId });
          }
          await dispatch(new SaveAsDraftCompleted());
        })
      );
  }),

  /**
   * The actual submit process, after pending background saves has been handled
   *
   * This turns the draft into a saved Case, making changes avaliable to other users
   *
   *
   */
  submitDocument: (): ActionDispatching<any, ApplicationState> => async (
    dispatch: any,
    getState: () => ApplicationState
  ) => {
    dispatch(new RequestCaseSave());

    let state = getState().caseArea as UiState;
    const translate = createDescriptionSelector(state.texts);
    const handleDraftErrors = createErrorHandler(dispatch, getState);
    const editing = state.editing;
    const { utcOffset } = getState().app.settings;
    try {
      const draftId = getDraftId(state.editing.values);

      await loadDraft(dispatch, draftId, state);

      state = getState().caseArea as UiState;

      const domainMetadata = state.editing.metadata && state.editing.metadata.domains || {};

      const { hasErrors, hasWarnings } = resolveHasAnyValidations(domainMetadata);

      if (hasErrors) {
        dispatch(new ErrorNotification(translate(caseTranslations.message_case_error)));
        dispatch(new CaseSaveFailed());
        return;
      }

      if (hasWarnings && !state.editing.warningsConfirmed) {
        const warnings = resolveWarnings(domainMetadata);
        dispatch(new ShowConfirmWarnings(true, warnings));
        dispatch(new CaseSaveFailed());
        return;
      }
      const useReverseConnection = !!state.editing.connectedDrafts?.[state.editing.connectedDrafts?.length-1]?.caseId;
      if(useReverseConnection) {
        connectDraftAndSubmit(dispatch, state, utcOffset, translate, draftId, useReverseConnection);
        return;
      }
      const caseId = await api.submitCase({
        caseDraftId: draftId,
        utcOffset
      });

      const casePermissions = await api.permissions(caseId);

      if (!readAccessToSubmittedCase(caseId, casePermissions)) {
        const successText = translate(caseTranslations.message_case_success);
        const notAuthorizedText = translate(
          caseTranslations.message_not_authorized_to_read_case
        );

        const toasts = toastFactory();
        toasts.dispatchSuccess(`${successText} - ${notAuthorizedText || ''}`);
      }

      dispatch(new LeaveDraftEdit());

      const { continueEditing } = state.editing;

      if (draftWillBeConnected(state)) {
        const prevItem = editing.connectedDrafts.slice(-1)[0];
        await connectedCaseApi.addCaseConnection(
          prevItem.draftId,
          caseId,
          prevItem.connectionTypeId,
          prevItem.sequence,
          prevItem.etag,
          useReverseConnection
        );
        navigate.toConnectionInitiator(getLocationOfInitiator(editing.connectedDrafts));
        const toasts = toastFactory({autoClose: AutoCloseDuration.Longer});
        const warningText = translate(caseTranslations.message_connected_cases_warning1);
        toasts.dispatchWarning(warningText);
      } else {
        !!continueEditing ? 
          isCaseViewModalOpen() ? navigate.toCaseEditInModal(caseId) : navigate.toCaseEdit(caseId) : 
          isCaseViewModalOpen() ? navigate.toCaseViewModal(caseId) : navigate.toCaseView(caseId);
        }
    } catch (err) {
      handleDraftErrors(err, translate(caseTranslations.message_case_error));
      dispatch(
        new CaseSaveFailed()
      );
    }
  },

  /**
   * Confirms that the user accepts all warnings and wants to go on with the submit
   */
  confirmWarnings: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new ShowConfirmWarnings(false));
      dispatch(new WarningsConfirmed(true));
      dispatch(actionCreators.submitDocument());
    }),

  /**
   * User cancelled the submit from confirm warnings dialog. Go back to edit.
   */
  unconfirmWarnings: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new ShowConfirmWarnings(false));
      dispatch(new WarningsConfirmed(false));
    })
};
